import React from 'react'

import { ChevronUp } from 'lucide-react'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { DeliveryStatus, SitePermitStatus } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'

import Localization from '../../localization/LocalizationManager'
import Delivery from '../../models/Delivery'
import Message from '../../models/Message'
import Workflow from '../../models/Workflow'
import BaseAssignmentsStore from '../../stores/BaseAssignments.store'
import BaseFollowingsStore from '../../stores/BaseFollowings.store'
import BaseActionBarStore from '../../stores/ui/BaseActionBar.store'
import MenuCloser from '../MenuCloser'
import MessagesActionBar from './MessagesActionbar'

export type statusType = SitePermitStatus | DeliveryStatus

// INTERMEDIATE (BESPOKE). The resize/dismiss gesture below is hand-rolled on
// pointer events, not the canonical vaul Drawer, and is meant to be converted
// under its own ticket once the three blockers are solved: vaul content is
// position:fixed (viewport-scoped) while this composer must stay inside the
// 400px panel; vaul has no collapsed in-flow state, but the unfocused composer
// participates in the panel's layout; and an always-open drawer over the map
// risks the body position-lock collapsing the AutoSizer (STX1-443). Until then
// prefer fixing this in place over widening it — it is deliberately a stopgap.

// The focused composer's resting height, and the floor the drag handle can
// shrink it back to.
const MIN_COMPOSER_HEIGHT = 220
const MAX_COMPOSER_HEIGHT_RATIO = 0.95
// Dragging the handle below the resting floor shrinks the composer down to
// DRAG_FLOOR; releasing under DISMISS_HEIGHT closes it instead of snapping back.
const DRAG_FLOOR_HEIGHT = 120
const DISMISS_HEIGHT = 170

interface IProps {
  store: BaseActionBarStore
  shouldShowStatusSelector: boolean

  applyStatusAndStep(status: statusType, stepId: string): void
  applyMessage?(): void
  renderButtons(
    renderStatusBtn: (
      btnText: string,
      isValid: boolean,
      status: statusType,
      isDeny?: boolean,
      customBtnHandler?: () => void,
    ) => JSX.Element,
  ): JSX.Element
  // Optional: DeliveryDetails mounts its own selector instead.
  renderStatusSelectMenu?(
    setStatusAndStep: (status: statusType, stepId: string) => void,
    statusSelectorAnchor: HTMLElement,
  ): JSX.Element
  toggleStatusSelectMenu(): void

  assignmentsStore: BaseAssignmentsStore
  followingsStore: BaseFollowingsStore
  isReadOnly: boolean
  entity: Message | Delivery | Workflow
  customRenderer?: (status: statusType, stepId: string) => JSX.Element
  informationalTextElement?: JSX.Element
  isMobileMode?: boolean
  entityStatus?
}

@observer
export default class ActionBar extends React.Component<IProps> {
  @observable private isFocused: boolean = false
  @observable private entity: Message | Delivery | Workflow = null
  @observable private containerRef: HTMLDivElement = null
  @observable private statusSelectorAnchor: HTMLElement = null
  @observable private composerHeight: number = null
  @observable private isResizing: boolean = false
  private formRef: HTMLFormElement = null
  private composerEl: HTMLDivElement = null
  private resizeStartY: number = 0
  private resizeStartHeight: number = 0
  private resizeMaxHeight: number = Infinity

  public constructor(props: IProps) {
    super(props)
    this.entity = props.entity
    makeObservable(this)
  }

  public componentWillUnmount() {
    this.stopTrackingResize()
  }

  public componentDidUpdate(prevProps: IProps) {
    if (this.props.entity !== prevProps.entity) {
      runInAction(() => {
        this.entity = this.props.entity
      })
    }
  }

  public render() {
    return (
      <MenuCloser
        isOpen={this.isFocused}
        closeMenu={this.setIsFocused.bind(null, false)}
      >
        <div
          ref={this.setComposerRef}
          style={this.composerStyle}
          className={classList({
            'bg-white full-width': true,
            // Focused, the composer lifts out of the flow and overlays the form
            // bottom — what ActionDraggableBar's absolute box used to do. It
            // must stay unpositioned otherwise: LinkedWorkflowPicker's sheet is
            // absolute against the surrounding view, not against the composer.
            // No overflow clip: the mention suggestions popup is absolute
            // against this box and deliberately grows past its top edge.
            // Panel-edge shadow convention, rotated: this overlays from below.
            'tw:absolute tw:inset-x-0 tw:bottom-0 tw:z-[11] tw:flex tw:flex-col tw:rounded-t-[10px] tw:shadow-[0_-4px_16px_rgba(0,0,0,0.1)]':
              this.isFocused,
          })}
        >
          {this.isFocused && (
            <div
              className="tw:flex tw:shrink-0 tw:cursor-grab tw:touch-none tw:justify-center tw:rounded-t-[inherit] tw:border-0 tw:border-t tw:border-solid tw:border-border tw:py-2.5 tw:active:cursor-grabbing"
              onPointerDown={this.onResizeStart}
            >
              <div className="tw:h-1 tw:w-[100px] tw:rounded-full tw:bg-muted" />
            </div>
          )}
          {this.renderContent()}
        </div>
      </MenuCloser>
    )
  }

  @action.bound
  private toggleStatusSelect() {
    this.setIsFocused(false)
    this.formRef?.blur()
    this.props.toggleStatusSelectMenu()
  }

  private renderContent() {
    const {
      renderStatusSelectMenu,
      store,
      renderButtons,
      customRenderer,
      isReadOnly,
      assignmentsStore,
      followingsStore,
      informationalTextElement,
      isMobileMode,
      shouldShowStatusSelector,
      applyMessage,
    } = this.props

    const { isFileUploading, isImageUploading } = store

    return (
      <div
        className={classList({
          'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:p-2.5': true,
          'tw:h-full': !this.isFocused,
          'inactive-element': isFileUploading || isImageUploading,
          'tw:border-0 tw:border-t tw:border-solid tw:border-border':
            !this.isFocused,
        })}
      >
        {!isReadOnly && (
          <div className="tw:flex tw:shrink-0 tw:flex-col">
            {informationalTextElement}
            <div className="tw:flex tw:items-center tw:gap-2">
              {/* Primary actions fill the row; only the caret is reserved on
                  the right. `[&>*]:flex-1` restores what the legacy `.row`
                  gave these buttons via `.row > * { flex: 1 }`. */}
              <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:[&>*]:flex-1">
                {renderButtons(this.renderStatusBtn)}
              </div>
              {shouldShowStatusSelector && (
                <Button
                  ref={this.setStatusSelectorAnchor}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={Localization.translator.updateStatus}
                  className="tw:text-foreground"
                  onClick={this.toggleStatusSelect}
                >
                  <ChevronUp />
                </Button>
              )}
            </div>
          </div>
        )}
        {renderStatusSelectMenu?.(
          this.setStatusAndStep,
          this.statusSelectorAnchor,
        )}
        <div
          className={classList({
            'tw:flex tw:min-h-0 tw:flex-col tw:overflow-hidden': true,
            // Focused it fills the composer; otherwise it hugs its content.
            'tw:flex-1': this.isFocused,
            'tw:flex-initial': !this.isFocused,
          })}
          ref={this.setContainerRef}
        >
          <MessagesActionBar
            actionBarStore={store}
            entity={this.entity}
            customStatusRenderer={customRenderer}
            assignmentsStore={assignmentsStore}
            followingsStore={followingsStore}
            containerRef={this.containerRef}
            isFocused={this.isFocused}
            formsRef={this.formRef}
            setIsFocused={this.setIsFocused}
            setFormsRef={this.setFormsRef}
            isMobileMode={isMobileMode}
            applyMessage={applyMessage}
          />
        </div>
      </div>
    )
  }

  @action.bound
  public setFormsRef(value) {
    this.formRef = value
  }

  @action.bound
  public setIsFocused(value: boolean) {
    this.isFocused = value
    if (!value) {
      this.composerHeight = null
      this.props.store.closeComposerPopups()
    }
  }

  private get composerStyle(): React.CSSProperties {
    if (!this.isFocused) {
      return undefined
    }
    return {
      height: this.composerHeight || undefined,
      // The floor is lifted while dragging so the box can follow the pointer
      // below it and read as dismissible.
      minHeight: this.isResizing ? undefined : MIN_COMPOSER_HEIGHT,
      maxHeight: `${MAX_COMPOSER_HEIGHT_RATIO * 100}%`,
    }
  }

  private setComposerRef = (ref: HTMLDivElement) => {
    this.composerEl = ref
  }

  @action.bound
  private onResizeStart(event: React.PointerEvent<HTMLDivElement>) {
    if (!this.composerEl) {
      return
    }
    event.preventDefault()
    this.resizeStartY = event.clientY
    this.resizeStartHeight = this.composerEl.getBoundingClientRect().height
    const available = (this.composerEl.offsetParent as HTMLElement)
      ?.clientHeight
    this.resizeMaxHeight = available
      ? available * MAX_COMPOSER_HEIGHT_RATIO
      : Infinity
    // Pin the current height before dropping the min-height floor, otherwise
    // the box collapses to content height for a frame on the first drag.
    this.composerHeight = this.resizeStartHeight
    this.isResizing = true
    event.currentTarget.setPointerCapture(event.pointerId)
    window.addEventListener('pointermove', this.onResize)
    window.addEventListener('pointerup', this.onResizeEnd)
    // An OS/browser gesture takeover cancels the pointer instead of releasing
    // it; without this the move listener leaks and isResizing stays true.
    window.addEventListener('pointercancel', this.onResizeEnd)
  }

  @action.bound
  private onResize(event: PointerEvent) {
    this.composerHeight = Math.min(
      Math.max(
        this.resizeStartHeight + this.resizeStartY - event.clientY,
        DRAG_FLOOR_HEIGHT,
      ),
      this.resizeMaxHeight,
    )
  }

  private stopTrackingResize() {
    window.removeEventListener('pointermove', this.onResize)
    window.removeEventListener('pointerup', this.onResizeEnd)
    window.removeEventListener('pointercancel', this.onResizeEnd)
  }

  @action.bound
  private onResizeEnd() {
    this.stopTrackingResize()

    if (!this.isResizing) {
      return
    }
    this.isResizing = false

    if (this.composerHeight && this.composerHeight < DISMISS_HEIGHT) {
      this.dismissComposer()
      return
    }
    if (this.composerHeight && this.composerHeight < MIN_COMPOSER_HEIGHT) {
      this.composerHeight = MIN_COMPOSER_HEIGHT
    }
  }

  private dismissComposer() {
    // Drop the caret too, so the gesture also puts away the mobile keyboard.
    const focused = document.activeElement as HTMLElement
    if (focused && this.composerEl?.contains(focused)) {
      focused.blur()
    }
    this.setIsFocused(false)
  }

  @action.bound
  private setContainerRef(ref: HTMLDivElement) {
    this.containerRef = ref
  }

  @action.bound
  private setStatusSelectorAnchor(ref: HTMLElement) {
    this.statusSelectorAnchor = ref
  }

  private setStatusAndStep = (status: statusType, stepId: string): void => {
    this.props.applyStatusAndStep(status, stepId)
  }

  private renderStatusBtn = (
    btnText: string,
    isValid: boolean,
    status: statusType,
    isDeny?: boolean,
    customBtnHandler?: () => void,
  ): JSX.Element => {
    return (
      <Button
        variant={isDeny ? 'outline' : 'default'}
        className="tw:flex-1"
        disabled={!isValid}
        onClick={this.onStatusClick.bind(this, status, customBtnHandler)}
      >
        {btnText}
      </Button>
    )
  }

  private onStatusClick(status: statusType, customBtnHandler?: () => void) {
    if (!customBtnHandler) {
      this.setStatusAndStep(status, null)
      return
    }

    customBtnHandler()
  }
}
